import { useState } from 'react';
import { AlertTriangle, Clock } from 'lucide-react';
import { SortableHeader, nextSort, type SortState } from '../ui/SortableHeader';
import { ColumnFilter } from '../ui/ColumnFilter';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { useColumnFilters } from '../../hooks/useColumnFilters';
import { EntryExclusionDialog, type ExclusionTarget } from './EntryExclusionDialog';
import { useVatLang } from './VatLang';
import type { ExclusionAction, ExclusionView, VatCompany } from '../../types/vatReporting';

/**
 * "Nachzügler aus früheren Perioden" — the postings deferred in an earlier month that are
 * still in no declaration at all.
 *
 * ONLY RENDERED WHEN THERE ARE SOME, per the plan. That is not the same as being silent about
 * the zero case: the `CARRY_FORWARD_OPEN` control is always on the page and says "nothing is
 * waiting" when the list is empty. A panel is a work list; a control is a statement.
 *
 * TWO ACTIONS PER ROW AND NO THIRD. "Übernehmen" puts the posting into the month currently on
 * screen, in full; "endgültig nicht melden" closes it with a reason. There is deliberately no
 * "leave it" button — leaving it is what happens by not clicking, and the control counts how
 * long that has been going on.
 */

interface Props {
    company: VatCompany;
    /** The month on screen — the one "übernehmen" carries a posting INTO. */
    year: number;
    month: number;
    entries: ExclusionView[];
    canEdit: boolean;
    /** True when the month on screen is filed: it can take nothing in. */
    periodLocked: boolean;
    onChanged: () => void;
}

const money = (v: number | null) =>
    v === null || v === undefined
        ? '—'
        : new Intl.NumberFormat('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

const periodName = (y: number, m: number) => `${y}-${String(m).padStart(2, '0')}`;

type ColKey = 'origin' | 'age' | 'entryNo' | 'documentNo' | 'counterparty'
    | 'kennzahl' | 'contribution' | 'comment';

const COLUMNS: { key: ColKey; labelKey: string; width: number; align?: 'right' }[] = [
    { key: 'origin', labelKey: 'carry.origin', width: 100 },
    { key: 'age', labelKey: 'carry.age', width: 80, align: 'right' },
    { key: 'entryNo', labelKey: 'drill.entry', width: 80 },
    { key: 'documentNo', labelKey: 'drill.document', width: 140 },
    { key: 'counterparty', labelKey: 'drill.counterparty', width: 200 },
    { key: 'kennzahl', labelKey: 'ustva.kz', width: 60 },
    { key: 'contribution', labelKey: 'carry.contribution', width: 120, align: 'right' },
    { key: 'comment', labelKey: 'carry.comment', width: 280 },
];

const ACCESSOR: Record<ColKey, (r: ExclusionView) => string | number | null> = {
    origin: r => periodName(r.originYear, r.originMonth),
    age: r => r.ageMonths,
    entryNo: r => r.entryNo,
    documentNo: r => r.documentNo,
    counterparty: r => r.counterpartyName ?? r.counterpartyNo,
    kennzahl: r => r.kennzahl,
    contribution: r => r.contribution,
    comment: r => r.comment,
};

const NUMERIC: ColKey[] = ['age', 'entryNo', 'contribution'];

const FILTER_COLUMNS = COLUMNS.map(c => ({
    key: c.key,
    accessor: ACCESSOR[c.key],
    numeric: NUMERIC.includes(c.key),
}));

export function CarryForwardPanel({
    company, year, month, entries, canEdit, periodLocked, onChanged,
}: Props) {
    const { t } = useVatLang();
    const [sort, setSort] = useState<SortState<ColKey>>({ key: 'origin', direction: 'asc' });
    const [dialog, setDialog] = useState<{ action: ExclusionAction; target: ExclusionTarget } | null>(null);

    const { widthOf, resizingKey, startResize, resetWidth, shouldSuppressClick } =
        useResizableColumns(COLUMNS, 'vat-carry-col-widths');
    const { filtered, filters, setColumnFilter, isFiltered, activeCount, clearAll, uniqueValuesFor } =
        useColumnFilters(entries, FILTER_COLUMNS);
    const filterLabels = {
        search: t('filter.search'), all: t('filter.all'), none: t('filter.none'),
        from: t('filter.from'), to: t('filter.to'), empty: t('filter.empty'),
    };

    const onSort = (key: ColKey) => {
        if (shouldSuppressClick()) return;
        setSort(s => nextSort(s, key, NUMERIC.includes(key)));
    };

    // Rendered by the caller only when there are some; guarded here too so the component is
    // safe to drop anywhere.
    if (entries.length === 0) return null;

    const sorted = sort.key
        ? [...filtered].sort((a, b) => {
            const acc = ACCESSOR[sort.key as ColKey];
            const av = acc(a);
            const bv = acc(b);
            if (av === null || av === undefined || av === '') return 1;
            if (bv === null || bv === undefined || bv === '') return -1;
            const base = typeof av === 'number' && typeof bv === 'number'
                ? av - bv
                : String(av).localeCompare(String(bv), undefined, { numeric: true });
            return base * (sort.direction === 'asc' ? 1 : -1);
        })
        : filtered;

    const total = filtered.reduce((s, r) => s + (r.contribution ?? 0), 0);
    const oldest = entries.reduce((max, r) => Math.max(max, r.ageMonths), 0);

    const openDialog = (action: ExclusionAction, r: ExclusionView) => setDialog({
        action,
        target: {
            entryNo: r.entryNo,
            documentNo: r.documentNo,
            counterpartyName: r.counterpartyName,
            kennzahl: r.kennzahl,
            contribution: r.contribution,
            originYear: r.originYear,
            originMonth: r.originMonth,
            originComment: r.comment,
        },
    });

    return (
        <div className="border border-amber-300 rounded-lg bg-white overflow-hidden">
            <div className="px-4 py-2.5 border-b bg-amber-50 flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-sm font-semibold text-amber-900 flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    {t('carry.title')}
                    <span className="font-normal text-amber-800">
                        {t('carry.subtitle', {
                            count: entries.length, amount: money(total), oldest,
                        })}
                    </span>
                </h3>
                {periodLocked && (
                    <span className="text-xs text-amber-800">{t('carry.periodFiled')}</span>
                )}
            </div>

            {activeCount > 0 && (
                <div className="flex items-center justify-between px-4 py-1.5 border-b bg-amber-50 text-xs text-amber-900">
                    <span>{t('filter.active', { count: activeCount })}</span>
                    <button onClick={clearAll} className="text-blue-600 hover:text-blue-800 font-medium">
                        {t('filter.clearAll')}
                    </button>
                </div>
            )}

            <div className="overflow-auto" style={{ maxHeight: '40vh' }}>
                <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                        {COLUMNS.map(c => <col key={c.key} style={{ width: widthOf(c.key) }} />)}
                        <col style={{ width: 210 }} />
                    </colgroup>
                    <thead>
                        <tr className="border-b">
                            {COLUMNS.map(c => (
                                <SortableHeader
                                    key={c.key}
                                    label={t(c.labelKey)}
                                    sortKey={c.key}
                                    sort={sort}
                                    onSort={onSort}
                                    align={c.align}
                                    onResizeStart={x => startResize(c.key, x)}
                                    onResetWidth={() => resetWidth(c.key)}
                                    isResizing={resizingKey === c.key}
                                    filter={
                                        <ColumnFilter
                                            label={t(c.labelKey)}
                                            values={uniqueValuesFor(c.key)}
                                            state={filters[c.key]}
                                            onChange={s => setColumnFilter(c.key, s)}
                                            numeric={NUMERIC.includes(c.key)}
                                            active={isFiltered(c.key)}
                                            labels={filterLabels}
                                        />
                                    }
                                />
                            ))}
                            <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-left text-xs font-medium text-gray-500">
                                {t('carry.actions')}
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {sorted.map(r => (
                            <tr key={r.entryNo} className="hover:bg-gray-50">
                                <td className="px-3 py-1.5 whitespace-nowrap font-medium">
                                    {periodName(r.originYear, r.originMonth)}
                                </td>
                                <td className="px-3 py-1.5 text-right tabular-nums text-gray-600">
                                    {t('carry.months', { count: r.ageMonths })}
                                </td>
                                <td className="px-3 py-1.5 text-gray-500 tabular-nums">{r.entryNo}</td>
                                <td className="px-3 py-1.5 truncate" title={r.documentNo ?? undefined}>
                                    {r.documentNo ?? (
                                        <span className="inline-flex items-center gap-1 text-red-600 text-xs">
                                            <AlertTriangle className="h-3 w-3" />{t('carry.entryMissing')}
                                        </span>
                                    )}
                                </td>
                                <td className="px-3 py-1.5 truncate"
                                    title={[r.counterpartyName, r.counterpartyNo].filter(Boolean).join(' · ')}>
                                    {r.counterpartyName ?? <span className="text-gray-400">—</span>}
                                </td>
                                <td className="px-3 py-1.5 font-mono">{r.kennzahl ?? '—'}</td>
                                <td className="px-3 py-1.5 text-right tabular-nums">{money(r.contribution)}</td>
                                <td className="px-3 py-1.5 text-gray-600 truncate" title={r.comment}>
                                    {r.comment}
                                    <span className="ml-1 text-xs text-gray-400">{r.createdBy}</span>
                                </td>
                                <td className="px-3 py-1.5">
                                    {canEdit && !periodLocked ? (
                                        <span className="inline-flex items-center gap-2">
                                            <button onClick={() => openDialog('include', r)}
                                                    className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                                                {t('carry.include', { period: periodName(year, month) })}
                                            </button>
                                            <button onClick={() => openDialog('drop', r)}
                                                    className="text-xs text-red-600 hover:text-red-800">
                                                {t('carry.drop')}
                                            </button>
                                            <button onClick={() => openDialog('reset', r)}
                                                    title={t('carry.resetHint', {
                                                        period: periodName(r.originYear, r.originMonth),
                                                    })}
                                                    className="text-xs text-gray-500 hover:text-gray-700">
                                                {t('carry.reset')}
                                            </button>
                                        </span>
                                    ) : (
                                        <span className="text-xs text-gray-400">
                                            {canEdit ? t('carry.periodFiledShort') : t('carry.readOnly')}
                                        </span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot className="bg-gray-50 border-t font-medium">
                        <tr>
                            <td className="px-3 py-2" colSpan={6}>
                                {activeCount > 0
                                    ? t('drill.filteredCount', { shown: filtered.length, total: entries.length })
                                    : t('drill.count', { count: entries.length })}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">{money(total)}</td>
                            <td />
                            <td />
                        </tr>
                    </tfoot>
                </table>
            </div>

            {dialog && (
                <EntryExclusionDialog
                    action={dialog.action}
                    company={company}
                    year={year}
                    month={month}
                    target={dialog.target}
                    onClose={() => setDialog(null)}
                    onDone={() => { setDialog(null); onChanged(); }}
                />
            )}
        </div>
    );
}
